import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SkipLink } from '../ui/SkipLink';
import { SiteHeader } from '../LowOnFuel/SiteHeader';
import { SiteFooter } from '../LowOnFuel/SiteFooter';
import { useAnnouncer } from '../ui/LiveRegion';

/**
 * Announces the new page to a screen reader after a client-side
 * navigation.
 *
 * Without this, following a link in a single-page app changes the whole
 * view silently: the browser's own page-load announcement never happens,
 * so a screen reader user is left on a page they were not told they had
 * arrived at. The delay lets the destination page set `document.title`
 * first — its effect runs after this one, because it is the parent.
 */
function RouteAnnouncer() {
  const {
    pathname
  } = useLocation();
  const {
    announce
  } = useAnnouncer();
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    // The initial page load is announced by the browser already.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      announce(`${document.title}. Page loaded.`);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [pathname, announce]);
  return null;
}

/**
 * The shell every page shares: skip link, header, one `main` landmark,
 * footer. Also resets scroll on navigation — a client-side route change
 * leaves the scroll position where it was, so a link from halfway down one
 * page lands halfway down the next.
 *
 * `main` carries the header offset so no page has to account for the fixed
 * header itself.
 */
export function PageLayout({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return <div className="flex min-h-screen w-full flex-col bg-sand font-sans text-ink selection:bg-moss/30 selection:text-ink" data-id="element-25" data-mp="8xha4xzoxubc">
      <SkipLink data-id="element-26" data-mp="3y4dxmmxjaw3" />
      <RouteAnnouncer data-id="element-27" data-mp="3zp99eh2ixv0" />
      <SiteHeader data-id="element-28" data-mp="3z60oodb5ltn" />

      <main id="main-content" className="flex-1 pt-header-sm lg:pt-header-lg" data-id="element-29" data-mp="40qolv54a6ij">
        {children}
      </main>

      <SiteFooter data-id="element-30" data-mp="3vfj0gom07f2m" />
    </div>;
}